import { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import api from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";

export default function PaymentSuccess() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [status, setStatus] = useState("checking");
  const [payload, setPayload] = useState(null);
  const { refresh, user } = useAuth();

  useEffect(() => {
    if (!sessionId) { setStatus("error"); return; }
    let attempts = 0;
    let cancelled = false;

    const poll = async () => {
      while (!cancelled && attempts < 10) {
        try {
          const { data } = await api.get(`/checkout/status/${sessionId}`);
          setPayload(data);
          if (data.payment_status === "paid") {
            setStatus("paid");
            await refresh();
            return;
          }
          if (data.status === "expired") {
            setStatus("expired");
            return;
          }
        } catch (error) {
          // Ignore — Stripe sandbox occasionally 500s on status retrieval; webhook will still activate
          console.warn("Checkout status poll failed; will retry", error);
        }
        attempts++;
        await new Promise((r) => setTimeout(r, 2000));
      }
      if (!cancelled) {
        // Fall back: assume webhook processed; refresh user state and show pending confirmation
        await refresh();
        setStatus("pending");
      }
    };
    poll();
    return () => { cancelled = true; };
  }, [sessionId, refresh]);

  const home = user?.role === "tenant" ? "/tenant" : "/dashboard";

  return (
    <div className="min-h-screen bg-[#F8FAFC] flex items-center justify-center p-6">
      <div className="w-full max-w-md bg-white border border-slate-200 rounded-sm p-10 text-center">
        {status === "checking" && (
          <>
            <Loader2 className="w-12 h-12 mx-auto text-slate-900 animate-spin" />
            <h1 className="text-2xl font-bold tracking-tight mt-5" style={{fontFamily:"Outfit"}}>Confirming payment…</h1>
            <p className="text-sm text-slate-500 mt-2">This may take a few seconds.</p>
          </>
        )}
        {status === "paid" && (
          <>
            <CheckCircle className="w-12 h-12 mx-auto text-green-600" />
            <h1 className="text-2xl font-bold tracking-tight mt-5" style={{fontFamily:"Outfit"}}>Payment received</h1>
            <p className="text-sm text-slate-500 mt-2">
              {payload?.payment_type === "subscription" ? "Your subscription is active." : "Thank you — your rent has been recorded."}
            </p>
            <div className="mt-4 font-mono text-xl font-bold">${(payload?.amount_total / 100).toFixed(2)}</div>
            <Link to={home} data-testid="payment-return-button" className="btn-primary-dark inline-block mt-6">Return to dashboard</Link>
          </>
        )}
        {(status === "expired" || status === "error") && (
          <>
            <XCircle className="w-12 h-12 mx-auto text-red-500" />
            <h1 className="text-2xl font-bold tracking-tight mt-5" style={{fontFamily:"Outfit"}}>Payment not completed</h1>
            <p className="text-sm text-slate-500 mt-2">Please try again from billing.</p>
            <Link to={home} className="btn-outline-dark inline-block mt-6" data-testid="payment-return-button">Back</Link>
          </>
        )}
        {status === "pending" && (
          <>
            <CheckCircle className="w-12 h-12 mx-auto text-amber-500" />
            <h1 className="text-2xl font-bold tracking-tight mt-5" style={{fontFamily:"Outfit"}}>We're confirming your payment</h1>
            <p className="text-sm text-slate-500 mt-2">
              Your payment is processing. You can safely return to your dashboard — it will reflect shortly.
            </p>
            <Link to={home} data-testid="payment-return-button" className="btn-primary-dark inline-block mt-6">Return to dashboard</Link>
          </>
        )}
      </div>
    </div>
  );
}
